import React, { useState } from 'react';
import useIntl from '@afosto/utils-react/intl/useIntl';
import usePacking from '../../utils/usePacking';
import usePermissions from '@afosto/utils-react/permissions/usePermissions';
import usePrivate from '@afosto/auth-react/usePrivate';
import useQueryParameters from '@afosto/utils-react/useQueryParameters';
import { Link, useParams } from 'react-router-dom';
import Typography from '@material-ui/core/Typography';
import AlreadyPackedMessage from './AlreadyPackedMessage';
import CheckParcelsDialog from '../../components/CheckParcelsDialog';
import FinishedPackingDialog from './FinishedPackingDialog';
import Packing from '../../components/Packing';
import PickFirstMessage from './PickFirstMessage';
import Page from '@afosto/app-components-react/Page';
import translations from './translations';

const PackingView = () => {
  usePrivate();

  const intl = useIntl();
  const { shipmentID } = useParams();
  const { list_id: listID } = useQueryParameters();
  const [hasPermissions, UnauthorizedComponent] = usePermissions([
    'lcs:shipments:read',
    'lcs:shipments:write',
    'lcs:handling:write',
  ]);
  const packingContext = usePacking(shipmentID, true);
  const {
    isCreatingParcel,
    isDeletingParcel,
    isFetchingShipment,
    handleCheckParcels,
    handleCreateParcel,
    handleDeleteParcel,
    handlePackingDocuments,
    parcels,
    setShowCheckParcelsDialog,
    shipment,
    showCheckParcelsDialog,
  } = packingContext ?? {};
  const [showFinishedPackingDialog, setShowFinishedPackingDialog] = useState(false);

  const handleFinish = async (checkParcels = true) => {
    try {
      if (checkParcels) {
        await handleCheckParcels();
        return;
      }

      await handlePackingDocuments().catch(() => {});
      setShowFinishedPackingDialog(true);
    } catch (err) {
      // Do nothing.
    }
  };

  const handleCloseCheckParcels = () => {
    setShowCheckParcelsDialog(false);
    handleFinish(false).catch(() => {});
  };

  if (!hasPermissions()) {
    return (
      <Page title={shipment?.number ?? shipment.id}>
        <UnauthorizedComponent />
      </Page>
    );
  }

  const isShipmentHandled = shipment?.is_handled ?? false;
  const isShipmentPacked = shipment?.is_boxed ?? false;
  const isInvalidShipment =
    (!isFetchingShipment && !shipment) || (!!shipment && !shipment?.is_outbound);
  const isValidShipment =
    !!shipment && shipment?.is_outbound && isShipmentHandled && !isShipmentPacked;
  const showPickFirstMessage = !isFetchingShipment && !!shipment && !isShipmentHandled;
  const showAlreadyPackedMessage = !isFetchingShipment && !!shipment && isShipmentPacked;

  return (
    <>
      <Page
        isLoading={isFetchingShipment}
        title={shipment?.number}
        breadcrumbs={[
          {
            label: intl.formatMessage(translations.breadcrumbs.packing),
            path: `/packing${listID ? `?list_id=${listID}` : ''}`,
          },
          ...(shipment
            ? [
                {
                  label: shipment?.number ?? shipment?.id,
                  path: `/packing/${shipment?.id}`,
                  active: true,
                },
              ]
            : []),
        ]}
        linkComponent={Link}
        fullWidth
      >
        {isValidShipment && <Packing onFinish={handleFinish} packingContext={packingContext} />}
        {isInvalidShipment && <Typography variant="body2">Shipment not found</Typography>}
        {showPickFirstMessage && <PickFirstMessage shipment={shipment} />}
        {showAlreadyPackedMessage && <AlreadyPackedMessage shipment={shipment} />}
      </Page>
      <CheckParcelsDialog
        isLoading={isCreatingParcel || isDeletingParcel}
        isOpen={showCheckParcelsDialog}
        onContinue={handleCloseCheckParcels}
        onCreate={handleCreateParcel}
        onDelete={handleDeleteParcel}
        parcels={parcels}
      />
      <FinishedPackingDialog isOpen={showFinishedPackingDialog} />
    </>
  );
};

export default PackingView;
